/**
 * Migration Compatibility Layer
 * 
 * This file provides backward compatibility during the migration from VICUService to ClinicService.
 * It allows existing code to continue working while gradually transitioning to the new structure.
 * 
 * Usage:
 * 1. Replace `import VICUService from './VICUService'` with `import VICUService from './VICUServiceCompat'`
 * 2. Gradually update components to use ClinicService directly
 * 3. Remove this compatibility layer once migration is complete
 */

import ClinicService from "./ClinicService";
import PatientCohortService from "./PatientCohortService";
// import { cohortToLegacyVICU } from "../interfaces/Clinic"; // Removed as unused

/**
 * VICUServiceCompat - Compatibility wrapper for VICUService
 * Maps old VICU operations to new Clinic/Cohort operations
 */
class VICUServiceCompat {
    
    // ================================
    // Legacy VICU Methods (mapped to Clinics)
    // ================================
    
    static getAllVICU() {
        // Map to getAllClinics - this will need data transformation
        return ClinicService.getAllClinics(undefined, true);
    }

    static getOneVICU(vicu_id?: string) {
        // This needs to be handled differently since VICUs are now Cohorts
        if (vicu_id) {
            return ClinicService.getCohort(vicu_id);
        }
        return ClinicService.getAllClinics(undefined, true);
    }

    static addVICU(data: any, access_token: string) {
        console.warn("addVICU is deprecated. Use ClinicService.createCohort instead.");
        
        // Map VICU creation to Cohort creation
        // This assumes the data has been transformed appropriately
        const cohortData = {
            name: data.name || data.vicu_name,
            description: data.contact || data.contact_info || "",
            clinic: data.clinic_id || 1, // Default clinic ID - should be provided
            is_active: true
        };
        
        return ClinicService.createCohort(cohortData, access_token);
    }

    static getVICUPatients(vicu_id?: string) {
        if (vicu_id) {
            return PatientCohortService.getGroupPatients(vicu_id);
        }
        console.warn("getVICUPatients requires vicu_id parameter");
        return Promise.reject("Missing vicu_id parameter");
    }
    
    static getVICUDevices(vicu_id?: string) {
        console.warn("getVICUDevices is deprecated. VICUs now manage patients, not devices.");
        if (vicu_id) {
            return PatientCohortService.getGroupPatients(vicu_id);
        }
        return Promise.reject("Missing vicu_id parameter");
    }

    static getAvailableDevices() {
        console.warn("getAvailableDevices is deprecated. Use patient management instead.");
        // This would need to be mapped to unassigned patients
        return ClinicService.getAllClinics();
    }

    static manageVICUDevices(vicu_id: string, data: any, access_token: string) {
        console.warn("manageVICUDevices is deprecated. Use PatientCohortService instead.");
        
        // Map device operations to patient operations
        const operation = data.type === "attach" ? "attach" : "detach";
        const deviceIds = data.devices?.map((d: any) => d.device_id) || [];
        
        return PatientCohortService.manageDeviceAssignments(
            vicu_id, 
            operation, 
            deviceIds, 
            access_token
        );
    }

    static deleteVICU(vicu_id: string, access_token: string) {
        console.warn("deleteVICU is deprecated. Use ClinicService.deleteCohort instead.");
        return ClinicService.deleteCohort(vicu_id, access_token);
    }

    static editVICU(
        vicu_id: string, 
        name: string, 
        contact: string, 
        parmGranularity: number, 
        vicu_type: string, 
        access_token: string
    ) {
        console.warn("editVICU is deprecated. Use ClinicService.patchCohort instead.");
        
        const cohortData = {
            name: name,
            description: contact,
            // Note: parmGranularity and vicu_type would need to be stored elsewhere
            // or mapped to cohort properties as needed
        };
        
        return ClinicService.patchCohort(vicu_id, cohortData, access_token);
    }

    // ================================
    // Monitor Groups (mapped to Clinics/Cohorts)
    // ================================

    static getAllGroups() {
        return ClinicService.getAllGroups();
    }

    static getOneGroup(group_id: string) {
        return ClinicService.getOneGroup(group_id);
    }

    static getGroupDevices(group_id: string) {
        console.warn("getGroupDevices is deprecated. Use cohort patient management instead.");
        return ClinicService.getGroupDevices(group_id);
    }

    static getGroupPoolDevices(group_id: string) {
        console.warn("getGroupPoolDevices is deprecated. Use patient management instead.");
        return ClinicService.getClinicCohorts(group_id, true);
    }

    static manageGroupDevices(group_id: string, data: any, access_token: string) {
        console.warn("manageGroupDevices is deprecated. Use PatientCohortService instead.");
        return ClinicService.manageGroupDevices(group_id, data, access_token);
    }

    static getUserGroups(user_id: string) {
        console.warn("getUserGroups needs to be mapped to clinic access management.");
        // This would need to be implemented with the new access control system
        return ClinicService.getAllClinics();
    }

    static manageGroupAssignments(user_id: string, data: any, access_token: string) {
        console.warn("manageGroupAssignments is deprecated. Use clinic access management instead.");
        // This would need to be mapped to the new access control endpoints
        return Promise.reject("Not implemented - use clinic access management");
    }

    // ================================
    // Patient Cohort Management (updated to use new service)
    // ================================

    static getCohortPatients(cohort_id: string) {
        return PatientCohortService.getGroupPatients(cohort_id);
    }

    static getCohortPoolPatients(cohort_id: string) {
        console.warn("getCohortPoolPatients is deprecated. Use PatientCohortService.getCohortPatients instead.");
        return PatientCohortService.getGroupPatients(cohort_id);
    }

    static manageCohortPatients(cohort_id: string, data: any, access_token: string) {
        console.warn("manageCohortPatients is deprecated. Use PatientCohortService methods instead.");
        
        const operation = data.type;
        const patientIds = data.patients?.map((p: any) => p.patient_id) || [];
        const cohortIdNum = parseInt(cohort_id);
        
        if (operation === "attach") {
            return PatientCohortService.bulkAssignPatientsToCohort(
                cohortIdNum, 
                patientIds, 
                "Migrated assignment", 
                access_token
            );
        } else {
            return PatientCohortService.bulkRemovePatientsFromCohort(
                cohortIdNum, 
                patientIds, 
                access_token
            );
        }
    }
}

// Export as both named and default export for flexibility
export { VICUServiceCompat };
export default VICUServiceCompat;